/**
 * GuestbookOwnerList — 소유자 방명록 관리 UI
 */

import { useState } from 'react'
import { useGuestbookOwner, useGuestbookHide, useGuestbookDelete } from '@/hooks/useGuestbook.js'
import { Badge, ConfirmDialog } from '@/components/ui/index.js'
import { GuestbookEntryCard } from './GuestbookEntryCard.js'
import styles from '../GuestbookBlock.module.css'

interface GuestbookOwnerListProps {
  invitationId: string
}

export function GuestbookOwnerList({ invitationId }: GuestbookOwnerListProps) {
  const { entries, total, hiddenCount, privateCount, isLoading } = useGuestbookOwner(invitationId)
  const { toggle } = useGuestbookHide(invitationId)
  const { remove, isLoading: isDeleting } = useGuestbookDelete(invitationId)

  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)

  const handleDelete = async () => {
    if (!deleteTarget) return
    await remove(deleteTarget)
    setDeleteTarget(null)
  }

  if (isLoading) {
    return <p className={styles.ownerEmptyHint}>불러오는 중...</p>
  }

  if (entries.length === 0) {
    return <p className={styles.ownerEmptyHint}>아직 작성된 방명록이 없습니다</p>
  }

  return (
    <div className={styles.ownerSection}>
      <div className={styles.ownerStats}>
        <Badge>전체 {total}</Badge>
        {privateCount > 0 && <Badge color="blue">비공개 {privateCount}</Badge>}
        {hiddenCount > 0 && <Badge color="yellow">숨김 {hiddenCount}</Badge>}
      </div>

      <div className={styles.list}>
        {entries.map((entry) => (
          <GuestbookEntryCard
            key={entry.id}
            entry={entry}
            isOwnerView
            onHideToggle={toggle}
            onDelete={setDeleteTarget}
          />
        ))}
      </div>

      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="방명록 삭제"
        message="이 방명록을 삭제하시겠습니까? 삭제 후 복구할 수 없습니다."
        confirmLabel="삭제"
        variant="danger"
        loading={isDeleting}
      />
    </div>
  )
}
